import { useTranslation } from "react-i18next";
import { useCallback } from "react";

import type { IApiError, IApiErrorObject } from "../types";

import { useUserStore } from "@/stores/userStore";
import { notify } from "@/components/atoms/notify";

export const useErrorHandler = (
  onThrow: (message: string, status: number) => void,
) => {
  const { t } = useTranslation("error");

  const onError = useCallback(
    (err: string, errors?: IApiErrorObject[]) => {
      if (errors && errors?.length > 0) {
        for (const error of errors) {
          notify.error({ title: t("apiError.error"), text: error?.message });
        }
      } else {
        notify.error({ title: t("apiError.error"), text: err });
      }
    },
    [t],
  );

  return useCallback(
    async (error: IApiError) => {
      if (!error.response) {
        if (error.code !== "ERR_CANCELED") {
          // "ERR_CANCELED" happened if the file query or mutation has been canceled.
          // in this case we don't want to show an error message.
          onError(t("apiError.unable_server_response"));
        }
        return Promise.reject(error);
      }

      switch (error.response.status) {
        case 401:
          useUserStore.getState().logout();
          onError(t("apiError.401"), error?.response?.data);
          break;

        case 403:
          onError(t("apiError.403"), error?.response?.data);
          // onThrow(t("apiError.403"), 403);
          break;
        case 500:
          onError(t("apiError.500"), error?.response?.data);
          onThrow(t("apiError.500"), 500);
          break;
        case 400:
          onError(t("apiError.400"), error?.response?.data);
          break;
        case 503:
          onError(t("apiError.503"), error?.response?.data);
          onThrow(t("apiError.503"), 503);
          break;
        case 404:
          onError(t("apiError.404"), error?.response?.data);
          onThrow(t("apiError.404"), 404);
          break;

        case 406:
          onError(t("apiError.error"), error?.response?.data);
          break;

        case 422:
          onError(t("apiError.error"), error?.response?.data);
          break;

        default:
          onError(t("apiError.error"), error?.response?.data);
      }

      return Promise.reject(error);
    },
    [onError, onThrow, t],
  );
};
